<script setup>
import { router } from '@inertiajs/vue3';
import {
    ArrowLeftEndOnRectangleIcon,
    ArrowRightEndOnRectangleIcon,
    UserCircleIcon,
} from '@heroicons/vue/24/outline';
import JetDropdown from '@/Components/Dropdown.vue';
import JetDropdownLink from '@/Components/DropdownLink.vue';
import NavigationLabel from './NavigationLabel.vue';
import { utilityTrigger } from './navigationClasses.js';

defineProps({
    /** The authenticated user, or `null` for guests. */
    user: {
        type: Object,
        default: null,
    },
    canManageApiTokens: {
        type: Boolean,
        default: false,
    },
    isImpersonating: {
        type: Boolean,
        default: false,
    },
});

const logout = () => router.post(route('logout'));
const exitImpersonation = () => router.post(route('impersonate.stop'));

const isCurrent = name => route().current(name);
</script>

<template>
    <JetDropdown
        class="inline-flex"
        width="48"
    >
        <template #trigger>
            <span class="inline-flex">
                <button
                    type="button"
                    :class="utilityTrigger"
                >
                    <UserCircleIcon
                        class="shrink-0 size-6"
                        aria-hidden="true"
                    />

                    <NavigationLabel
                        class="whitespace-nowrap"
                        :title="$t('components.navigation.profile.main')"
                    />
                </button>
            </span>
        </template>

        <template #content>
            <div class="block text-xs text-deep-teal-500 purple-theme:text-purple-500 px-3 pt-2 pb-1">
                {{ $t('components.navigation.profile.main') }}
            </div>

            <template v-if="user">
                <JetDropdownLink
                    :href="route('dashboard')"
                    :active="isCurrent('dashboard')"
                >
                    {{ $t('components.navigation.profile.dashboard') }}
                </JetDropdownLink>
                <JetDropdownLink
                    :href="route('event-orders.index')"
                    :active="isCurrent('event-orders.index')"
                >
                    {{ $t('components.navigation.profile.orders') }}
                </JetDropdownLink>
                <JetDropdownLink
                    :href="route('basic-data.show')"
                    :active="isCurrent('basic-data.show')"
                >
                    {{ $t('components.navigation.profile.basicData') }}
                </JetDropdownLink>
                <JetDropdownLink
                    :href="route('profile.show')"
                    :active="isCurrent('profile.show')"
                >
                    {{ $t('components.navigation.profile.account') }}
                </JetDropdownLink>
                <JetDropdownLink
                    v-if="canManageApiTokens"
                    :href="route('api-tokens.index')"
                    :active="isCurrent('api-tokens.index')"
                >
                    {{ $t('components.navigation.profile.apiTokens') }}
                </JetDropdownLink>

                <div class="border-t border-gray-100" />

                <!-- Exit impersonation -->
                <form
                    v-if="isImpersonating"
                    @submit.prevent="exitImpersonation"
                >
                    <JetDropdownLink
                        as="button"
                        :with-icon="true"
                    >
                        <ArrowLeftEndOnRectangleIcon
                            class="shrink-0 size-5 mr-1.5"
                            aria-hidden="true"
                        />

                        {{ $t('components.navigation.profile.exitImpersonate') }}
                    </JetDropdownLink>
                </form>

                <!-- Authentication -->
                <form @submit.prevent="logout">
                    <JetDropdownLink
                        as="button"
                        :with-icon="true"
                    >
                        <ArrowRightEndOnRectangleIcon
                            class="shrink-0 size-5 mr-1.5"
                            aria-hidden="true"
                        />

                        {{ $t('components.navigation.profile.logout') }}
                    </JetDropdownLink>
                </form>
            </template>
            <template v-else>
                <JetDropdownLink :href="route('register')">
                    {{ $t('components.navigation.profile.register') }}
                </JetDropdownLink>
                <JetDropdownLink
                    :href="route('login')"
                    :with-icon="true"
                >
                    <ArrowLeftEndOnRectangleIcon
                        class="shrink-0 size-5 mr-1.5"
                        aria-hidden="true"
                    />

                    {{ $t('components.navigation.profile.login') }}
                </JetDropdownLink>
            </template>
        </template>
    </JetDropdown>
</template>
